import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { ApiService } from 'src/app/api.service';
import { UsuarioServiceProvider } from '../index.providers';
import { getPerformanceApiBase } from './mock/mock-config';
import { IsaAnalysisRequest, IsaAnalysisResponse, IsaTeamAnalysisRequest, PerformanceProcessResponse, ProcessSummary } from './proceso-desempeno.model';
import { LatestProcessesResponse, PerformanceAnnouncementsResponse, PerformanceHomeResponse, PerformanceProfileResponse } from './performance-home.model';
import { Goal, ObjectiveFormData, ObjectiveReviewSubmission, ObjectiveTrackingSubmission, TeamGoalMember, TeamObjectiveReviewSubmission } from './models/objetivos.model';
import { CompetencyEvaluation, CompetencyEvaluationTeam, SelfAssessmentSubmission, TeamCompetencyReviewSubmission } from './evaluacion-competencias.model';
import {
	MyPdiPlan,
	PdiAreaType,
	PdiDevelopmentGoal,
	PdiDevelopmentGoalSubmission,
	PdiGoalFormOptions,
	PdiMeeting,
	PdiMeetingSubmission,
	PdiResultDetail,
	PdiSchedulingOptions,
	PdiTeamResponse,
} from './pdi.model';
import {
	FeedbackDetailResponse,
	FeedbackDetailSession,
	FeedbackFiltersResponse,
	FeedbackMeetingRequest,
	FeedbackPointsPreviewResponse,
	FeedbackPointsProgress,
	FeedbackPointsRequest,
	FeedbackPointsResponse,
	FeedbackRecording,
	FeedbackRecordingResponse,
	FeedbackRecordingState,
	FeedbackSchedulingOptions,
	FeedbackTeamFilters,
	FeedbackTeamPage,
	MyFeedbackAcceptanceRequest,
	MyFeedbackResponse,
} from './feedback.model';
import { PerformanceSurvey, SurveySubmission } from './survey.model';
import {
	CalibrationDetail,
	CalibrationItemSubmission,
	CalibrationNineBox,
	CalibrationTeamMember,
} from './calibration.model';
import {
	TeamTrackingIsaSummaryResponse,
	TeamTrackingRevieweeResponse,
	TeamTrackingRiskResponse,
	TeamTrackingTeamSummaryResponse,
} from './team-tracking.model';

/**
 * Único punto de entrada HTTP del módulo de desempeño: un método por endpoint.
 * Los servicios de dominio consumen estos métodos (no llaman a HttpClient
 * directamente). Las URLs son las reales del backend (Scale); mientras el
 * backend se construye, el {@link PerformanceMockInterceptor} las responde con
 * data mock. Apagar `MOCK_PERFORMANCE_API` hace que estas mismas llamadas vayan
 * al backend real sin cambiar nada aquí ni en las vistas.
 *
 * El workspace/subworkspace se derivan de {@link UsuarioServiceProvider#data_usuario}
 * (datos del usuario logueado), para no hardcodearlos.
 */
@Injectable({
	providedIn: 'root',
})
export class PerformanceModuleApiService {
	/**
	 * Base del backend de desempeño. La envía el backend junto con el token
	 * (`baseUrl` en `platform-login-token`) y se cachea en localStorage; si no
	 * existe, cae al fallback. Es getter para tomar siempre el valor vigente.
	 */
	private get base(): string {
		return getPerformanceApiBase();
	}

	/** Workspace del usuario logueado (`data_usuario.workspace.slug`). */
	get workspaceSlug(): string {
		return this.up.data_usuario.value?.workspace?.slug;
	}

	/** Subworkspace (módulo) del usuario logueado (`data_usuario.module.slug`). */
	get subworkspaceSlug(): string {
		return this.up.data_usuario.value?.module?.slug;
	}

	constructor(
		private apiService: ApiService,
		private up: UsuarioServiceProvider,
		public http: HttpClient,
	) {}

	// ---- Envíos de evaluación con evidencias (multipart) --------------------

	/**
	 * Prepara el cuerpo de un envío de evaluación (objetivos o competencias).
	 *
	 * Sin evidencias adjuntas se manda el JSON tal cual (lo habitual). Si algún ítem
	 * trae archivos, el backend exige `multipart/form-data`: se aplana todo el
	 * payload a claves tipo `objectives[0][score]` y cada archivo se agrega como
	 * `objectives[0][attachments][]`. Es un solo request (los archivos no se suben
	 * aparte), y los interceptores globales respetan el `Content-Type` que genera el
	 * navegador al detectar un `FormData`.
	 */
	private buildReviewBody<T extends object>(payload: T): T | FormData {
		if (!this.hasFiles(payload)) return payload;

		const form = new FormData();
		Object.entries(payload).forEach(([key, value]) => this.appendFormValue(form, key, value));
		return form;
	}

	/** ¿El payload lleva al menos un archivo en alguno de sus niveles? */
	private hasFiles(value: any): boolean {
		if (!value || typeof value !== "object") return false;
		if (value instanceof File || value instanceof Blob) return true;
		if (Array.isArray(value)) return value.some((item) => this.hasFiles(item));
		return Object.values(value).some((item) => this.hasFiles(item));
	}

	/**
	 * Agrega un valor al `FormData` con la notación de arreglos/objetos de PHP.
	 * Los archivos de una colección van con sufijo `[]` (`attachments[]`), tal como
	 * los espera el backend; el resto de arreglos van indexados (`objectives[0]`)
	 * para no perder la correspondencia entre ítem y evidencia.
	 */
	private appendFormValue(form: FormData, key: string, value: any): void {
		if (value === null || value === undefined) return;

		if (value instanceof File) {
			form.append(key, value, value.name);
			return;
		}
		if (value instanceof Blob) {
			form.append(key, value);
			return;
		}
		if (Array.isArray(value)) {
			value.forEach((item, index) => {
				const isFile = item instanceof File || item instanceof Blob;
				this.appendFormValue(form, isFile ? `${key}[]` : `${key}[${index}]`, item);
			});
			return;
		}
		if (typeof value === "object") {
			Object.entries(value).forEach(([childKey, childValue]) =>
				this.appendFormValue(form, `${key}[${childKey}]`, childValue),
			);
			return;
		}
		form.append(key, typeof value === "boolean" ? (value ? "1" : "0") : String(value));
	}


	// ---- Procesos ----------------------------------------------------------

	/**
	 * @deprecated El Home real no expone un único `me/home`. Se arma con
	 * {@link getProfile} + {@link getLatestProcesses} (ver
	 * {@link PerformanceHomeService}). Se conserva por compatibilidad del mock.
	 */
	getPerformanceHome(): Observable<PerformanceHomeResponse> {
		return this.http.get<PerformanceHomeResponse>(`${this.base}v1/app/${this.workspaceSlug}/${this.subworkspaceSlug}/me/home`);
	}

	/**
	 * Resumen GLOBAL del usuario para el Home: datos básicos, indicadores (tiempo
	 * restante, revisión del equipo, cambios solicitados), su siguiente actividad
	 * pendiente y su organización (líder + equipo). Alimenta las métricas del
	 * header, la tarjeta "Organización" y la actividad en curso. El backend real
	 * envuelve la respuesta en `{ data }`.
	 */
	getProfile(): Observable<PerformanceProfileResponse> {
		return this.http.get<PerformanceProfileResponse>(
			`${this.base}v1/app/${this.workspaceSlug}/${this.subworkspaceSlug}/profile`,
		);
	}

	/**
	 * Procesos de desempeño más recientes del usuario (evaluado o evaluador) con su
	 * avance por etapa, para el bloque "Avance del proceso" del Home. Por defecto
	 * el backend devuelve los 3 más recientes (`limit`). El arreglo va en `data`.
	 */
	getLatestProcesses(limit = 3): Observable<LatestProcessesResponse> {
		return this.http.get<LatestProcessesResponse>(
			`${this.base}v1/app/${this.workspaceSlug}/${this.subworkspaceSlug}/processes/latest?limit=${limit}`,
		);
	}

	/**
	 * Anuncios generales del subworkspace para la sección "Novedades" del Home
	 * (bienvenidas, novedades, campañas), paginados y filtrados por vigencia. Antes
	 * el Home reutilizaba el endpoint de anuncios de la plataforma de aprendizaje;
	 * ahora el módulo de desempeño tiene su propio endpoint. El backend real
	 * responde paginado y envuelto: `{ data: { current_page, data: [...], ... } }`.
	 */
	getAnnouncements(page = 1, perPage = 15): Observable<PerformanceAnnouncementsResponse> {
		return this.http.get<PerformanceAnnouncementsResponse>(
			`${this.base}v1/app/${this.workspaceSlug}/${this.subworkspaceSlug}/announcements?page=${page}&per_page=${perPage}`,
		);
	}

	/** Lista los procesos de desempeño a los que el usuario tiene acceso. */
	getProcesses(): Observable<ProcessSummary[]> {
		return this.http.get<ProcessSummary[]>(`${this.base}v1/app/${this.workspaceSlug}/${this.subworkspaceSlug}/me/processes`);
	}

	/** Proceso con sus etapas y actividades. */
	getProcess(processId: number | string): Observable<PerformanceProcessResponse> {
		return this.http.get<PerformanceProcessResponse>(`${this.base}v1/app/${this.workspaceSlug}/${this.subworkspaceSlug}/me/processes/${processId}/show`);
	}

	/**
	 * Análisis de avance (ISA) del usuario autenticado en el proceso: lo genera con
	 * IA o devuelve el último del día. Alimenta la tarjeta de resumen de IsA.
	 *
	 * El evaluado se infiere del token (no va en el body), así que el análisis es
	 * siempre del usuario de la sesión. Con `stage_id` se acota a una etapa; sin él
	 * es general del proceso. Hay **uno por día por alcance**: llamadas repetidas
	 * devuelven el mismo registro salvo que se mande `forced`. Si el proceso (o la
	 * etapa) ya finalizó, nunca se genera uno nuevo.
	 */
	getIsaAnalysis(processId: number | string, payload: IsaAnalysisRequest = {}): Observable<IsaAnalysisResponse> {
		return this.http.post<IsaAnalysisResponse>(
			`${this.base}v1/app/${this.workspaceSlug}/${this.subworkspaceSlug}/me/processes/${processId}/isa-analysis`,
			payload,
		);
	}

	/**
	 * Análisis de avance (ISA) de **un evaluado a cargo** del líder autenticado, para
	 * las vistas de equipo (ej. el plan de objetivos de un integrante). Misma
	 * respuesta que {@link getIsaAnalysis}, pero el evaluado va en `reviewee_id` y
	 * debe estar asignado al evaluador de la sesión: si no le pertenece, el backend
	 * responde 403.
	 */
	getTeamIsaAnalysis(processId: number | string, payload: IsaTeamAnalysisRequest): Observable<IsaAnalysisResponse> {
		return this.http.post<IsaAnalysisResponse>(
			`${this.base}v1/app/${this.workspaceSlug}/${this.subworkspaceSlug}/team/processes/${processId}/isa-analysis`,
			payload,
		);
	}

	// ---- Objetivos ---------------------------------------------------------

	/**
	 * Objetivos del usuario autenticado en el proceso. El backend real responde
	 * paginado y envuelto: `{ data: { current_page, data: Goal[], ... } }`. La
	 * normalización (extraer el arreglo) la hace el servicio de dominio.
	 *
	 * El parámetro opcional `code` filtra los objetivos por etapa: con
	 * `code=evaluation` el backend devuelve solo MIS objetivos que pueden evaluarse
	 * (etapa de revisión/evaluación de objetivos).
	 */
	getPersonalObjectives(processId: number | string, code?: string): Observable<any> {
		const query = code ? `?code=${encodeURIComponent(code)}` : '';
		return this.http.get<any>(`${this.base}v1/app/${this.workspaceSlug}/${this.subworkspaceSlug}/me/processes/${processId}/objectives${query}`);
	}

	/**
	 * Envía la autoevaluación/revisión de MIS objetivos: una nota (`score`),
	 * comentario y evidencias (`attachments`) opcionales por cada objetivo del
	 * proceso, más un comentario general y la fecha de emisión opcionales. Con
	 * evidencias el cuerpo va como `multipart/form-data` (ver
	 * {@link buildReviewBody}). Devuelve la envoltura
	 * `{ data, message, type, http_code }` de la API real.
	 */
	submitObjectiveReviews(processId: number | string, payload: ObjectiveReviewSubmission): Observable<any> {
		return this.http.post<any>(
			`${this.base}v1/app/${this.workspaceSlug}/${this.subworkspaceSlug}/me/processes/${processId}/objective-reviews/store`,
			this.buildReviewBody(payload),
		);
	}

	/**
	 * Evaluación/autoevaluación de MIS objetivos ya realizada del usuario autenticado
	 * (si existe). Devuelve la envoltura `{ data, ... }` de la API real; `data.id` es
	 * `null` (y `objectives`/`details` vacíos) cuando aún no se ha evaluado.
	 */
	getObjectiveReviews(processId: number | string): Observable<any> {
		return this.http.get<any>(
			`${this.base}v1/app/${this.workspaceSlug}/${this.subworkspaceSlug}/me/processes/${processId}/objective-reviews/show`,
		);
	}

	/**
	 * Taxonomías para poblar el formulario de objetivo (unidades de medida, tipos
	 * de métrica con sus marcadores e tipos de evaluación). El backend real envuelve
	 * la respuesta en `{ data }`.
	 */
	getObjectiveFormData(processId: number | string): Observable<{ data: ObjectiveFormData }> {
		return this.http.get<{ data: ObjectiveFormData }>(
			`${this.base}v1/app/${this.workspaceSlug}/${this.subworkspaceSlug}/me/processes/${processId}/objectives/get-form-data`,
		);
	}

	/**
	 * Sugerencia de objetivo generada por IA (ISA) para el usuario autenticado.
	 * El body puede llevar `name` y/o `description` que el usuario ya escribió,
	 * para que la sugerencia se base en ellos, pero ambos son opcionales: ISA
	 * puede autocompletar el objetivo completo sin ningún input. El backend
	 * también considera el proceso, el perfil del usuario y los objetivos ya
	 * existentes. El backend real envuelve la respuesta en `{ data }`.
	 */
	getObjectiveIsaSuggestion(
		processId: number | string,
		payload: { name?: string; description?: string },
	): Observable<any> {
		return this.http.post<any>(
			`${this.base}v1/app/${this.workspaceSlug}/${this.subworkspaceSlug}/me/processes/${processId}/objectives/isa-suggestion`,
			payload,
		);
	}

	/**
	 * Sugerencia de objetivo generada por IA (ISA) para un evaluado (vista del
	 * líder). El body lleva el `reviewee_id` (requerido) y, opcionalmente, `name`
	 * y `description` que el líder ya escribió. El backend real envuelve la
	 * respuesta en `{ data }`.
	 */
	getTeamObjectiveIsaSuggestion(
		processId: number | string,
		payload: { reviewee_id: number | string; name?: string; description?: string },
	): Observable<any> {
		return this.http.post<any>(
			`${this.base}v1/app/${this.workspaceSlug}/${this.subworkspaceSlug}/team/processes/${processId}/objectives/isa-suggestion`,
			payload,
		);
	}


  //crud Mis objetivos
	createMyObjective(processId: number | string, goal: Goal): Observable<Goal> {
		return this.http.post<Goal>(`${this.base}v1/app/${this.workspaceSlug}/${this.subworkspaceSlug}/me/processes/${processId}/objectives/store`, goal);
	}

  updateMyObjective(processId: number | string, goalId: number | string, goal: Goal): Observable<Goal> {
		return this.http.put<Goal>(`${this.base}v1/app/${this.workspaceSlug}/${this.subworkspaceSlug}/me/processes/${processId}/objectives/${goalId}/update`, goal);
	}

	deleteMyObjective(processId: number | string, goalId: number | string): Observable<any> {
		return this.http.delete<any>(`${this.base}v1/app/${this.workspaceSlug}/${this.subworkspaceSlug}/me/processes/${processId}/objectives/${goalId}/delete`);
	}

	/**
	 * Registra un avance de seguimiento (tracking) de uno de MIS objetivos en la
	 * etapa de seguimiento: valor de avance, comentario opcional y hasta 5 evidencias.
	 * Con evidencias el cuerpo viaja como `multipart/form-data` (`attachments[]`); sin
	 * ellas sigue siendo JSON (lo decide {@link buildReviewBody}).
	 * Devuelve la envoltura `{ data, message, type, http_code }` de la API real.
	 */
	trackMyObjective(
		processId: number | string,
		objectiveId: number | string,
		payload: ObjectiveTrackingSubmission,
	): Observable<any> {
		return this.http.post<any>(
			`${this.base}v1/app/${this.workspaceSlug}/${this.subworkspaceSlug}/me/processes/${processId}/objectives/${objectiveId}/tracking/store`,
			this.buildReviewBody(payload),
		);
	}

	/**
	 * Lista los seguimientos (tracking) ya registrados de uno de MIS objetivos,
	 * paginados y ordenados del más reciente al más antiguo. Devuelve la envoltura
	 * paginada `{ data: { current_page, data: ObjectiveTracking[], ... }, ... }` de
	 * la API real; la normalización (extraer el arreglo) la hace el servicio de dominio.
	 */
	getMyObjectiveTrackings(
		processId: number | string,
		objectiveId: number | string,
		page = 1,
		perPage = 15,
	): Observable<any> {
		return this.http.get<any>(
			`${this.base}v1/app/${this.workspaceSlug}/${this.subworkspaceSlug}/me/processes/${processId}/objectives/${objectiveId}/tracking/list?page=${page}&per_page=${perPage}`,
		);
	}

	/**
	 * Actualiza un seguimiento (tracking) ya registrado de uno de MIS objetivos:
	 * nuevo valor de avance, comentario, evidencias nuevas (`attachments`) y las que
	 * se quitan (`remove_attachment_ids`). Devuelve la envoltura
	 * `{ data, message, type, http_code }` de la API real.
	 *
	 * Si el envío lleva evidencias, va como POST con `_method=PUT`: PHP no parsea el
	 * cuerpo multipart de un PUT (mismo caso que {@link updateSelfAssessment}).
	 */
	updateMyObjectiveTracking(
		processId: number | string,
		objectiveId: number | string,
		trackingId: number | string,
		payload: ObjectiveTrackingSubmission,
	): Observable<any> {
		const url = `${this.base}v1/app/${this.workspaceSlug}/${this.subworkspaceSlug}/me/processes/${processId}/objectives/${objectiveId}/tracking/${trackingId}/update`;
		const body = this.buildReviewBody(payload);

		if (body instanceof FormData) {
			body.append("_method", "PUT");
			return this.http.post<any>(url, body);
		}
		return this.http.put<any>(url, body);
	}

	/**
	 * Elimina un seguimiento (tracking) de uno de MIS objetivos. Devuelve la
	 * envoltura `{ message, type, http_code }` de la API real.
	 */
	deleteMyObjectiveTracking(
		processId: number | string,
		objectiveId: number | string,
		trackingId: number | string,
	): Observable<any> {
		return this.http.delete<any>(
			`${this.base}v1/app/${this.workspaceSlug}/${this.subworkspaceSlug}/me/processes/${processId}/objectives/${objectiveId}/tracking/${trackingId}/delete`,
		);
	}

	// ---- Seguimiento del equipo (rol líder) ---------------------------------

	/**
	 * Objetivos de MI EQUIPO que no van a llegar: compara el avance informado de
	 * cada objetivo con el que el calendario pide a hoy. Solo devuelve los
	 * evaluados a cargo del líder autenticado.
	 *
	 * Ojo con `level: null` — el período sale de las fechas del objetivo y, si no
	 * las tiene, de las de la etapa de seguimiento; sin ninguna de las dos no se
	 * pudo calcular, que **no** es lo mismo que ir bien.
	 */
	getTeamTrackingRisk(processId: number | string): Observable<TeamTrackingRiskResponse> {
		return this.http.get<TeamTrackingRiskResponse>(
			`${this.base}v1/app/${this.workspaceSlug}/${this.subworkspaceSlug}/team/processes/${processId}/trackings/risk`,
		);
	}

	/**
	 * Resumen de ISA del seguimiento de MI EQUIPO: dónde poner la atención, con las
	 * personas a atender primero y qué hacer con cada una.
	 *
	 * No se genera en cada llamada: devuelve el último resumen mientras el estado
	 * del equipo no haya cambiado (`reason: "unchanged"`) y se rehace como mucho
	 * una vez por día (`already_today`). Con `forced` se regenera igual. Si el
	 * proceso apagó la funcionalidad, `enabled` llega en false y no hay registro.
	 */
	getTeamTrackingIsaSummary(
		processId: number | string,
		forced = false,
	): Observable<TeamTrackingIsaSummaryResponse> {
		const query = forced ? '?forced=1' : '';
		return this.http.get<TeamTrackingIsaSummaryResponse>(
			`${this.base}v1/app/${this.workspaceSlug}/${this.subworkspaceSlug}/team/processes/${processId}/trackings/isa-summary${query}`,
		);
	}

	/**
	 * Equipo del líder para el listado "Seguimiento de objetivos - Mi equipo":
	 * totales del equipo y una fila por evaluado (avatar, nombre y cuántos
	 * objetivos tiene). Devuelve el equipo **entero**, sin paginar, así que el
	 * buscador de la vista filtra en el front.
	 */
	getTeamTrackingSummary(processId: number | string): Observable<TeamTrackingTeamSummaryResponse> {
		return this.http.get<TeamTrackingTeamSummaryResponse>(
			`${this.base}v1/app/${this.workspaceSlug}/${this.subworkspaceSlug}/team/processes/${processId}/trackings/summary`,
		);
	}

	/**
	 * Seguimiento de UNA persona del equipo: su avance por objetivo (`progress`,
	 * `submissions`) y el historial de envíos con sus evidencias
	 * (`submissions_list`). No trae la definición del objetivo (descripción,
	 * `finishes_at`, escala de umbrales): eso lo aporta
	 * {@link getProcessObjectives}, y la vista cruza las dos por `objective_id`.
	 */
	getRevieweeTrackings(
		processId: number | string,
		revieweeId: number | string,
	): Observable<TeamTrackingRevieweeResponse> {
		return this.http.get<TeamTrackingRevieweeResponse>(
			`${this.base}v1/app/${this.workspaceSlug}/${this.subworkspaceSlug}/team/processes/${processId}/reviewees/${revieweeId}/trackings`,
		);
	}

	/**
	 * Registra un avance de seguimiento sobre el objetivo de un evaluado (rol
	 * líder). Espejo del que usa el evaluado sobre sus propios objetivos
	 * ({@link trackMyObjective}): mismo cuerpo
	 * (`self_review_progress_value`, `self_review_progress_comment`,
	 * `attachments[]`, `remove_attachment_ids`) y mismo transporte —multipart si
	 * hay evidencias, JSON si no—.
	 *
	 * ⚠️ **El endpoint todavía no existe en el backend**: hoy lo responde el mock
	 * (`performance-mock.routes.ts`), con la forma que devolverá la API real. Cuando
	 * se publique no hay que tocar ni la vista ni el servicio, solo apagar
	 * `MOCK_PERFORMANCE_API`.
	 */
	trackTeamObjective(
		processId: number | string,
		revieweeId: number | string,
		objectiveId: number | string,
		payload: ObjectiveTrackingSubmission,
	): Observable<any> {
		return this.http.post<any>(
			`${this.base}v1/app/${this.workspaceSlug}/${this.subworkspaceSlug}/team/processes/${processId}/reviewees/${revieweeId}/objectives/${objectiveId}/tracking/store`,
			this.buildReviewBody(payload),
		);
	}

  /**
	 * Objetivos del plan de un integrante del equipo (vista del líder). Se filtra
	 * por `reviewee_id` (el integrante seleccionado). El backend responde paginado y
	 * envuelto: `{ data: { data: [...], ... } }`; la normalización la hace el servicio.
	 */
	getProcessObjectives(processId: number | string, revieweeId?: number | string | null): Observable<any> {
		const query = revieweeId != null && revieweeId !== '' ? `?reviewee_id=${revieweeId}` : '';
		return this.http.get<any>(`${this.base}v1/app/${this.workspaceSlug}/${this.subworkspaceSlug}/team/processes/${processId}/objectives${query}`);
	}

	/**
	 * Taxonomías para poblar el formulario de objetivo de un EVALUADO (vista del
	 * líder). Mismo shape que el form-data personal, pero valida que `reviewee_id`
	 * pertenezca al evaluador (403 si no). El backend envuelve en `{ data }`.
	 */
	getTeamObjectiveFormData(processId: number | string, revieweeId: number | string | null): Observable<any> {
		return this.http.get<any>(
			`${this.base}v1/app/${this.workspaceSlug}/${this.subworkspaceSlug}/team/processes/${processId}/objectives/get-form-data?reviewee_id=${revieweeId}`,
		);
	}

	/** Crea un objetivo para un evaluado (vista del líder). El body incluye `reviewee_id`. */
	createTeamObjective(processId: number | string, body: any): Observable<any> {
		return this.http.post<any>(
			`${this.base}v1/app/${this.workspaceSlug}/${this.subworkspaceSlug}/team/processes/${processId}/objectives/store`,
			body,
		);
	}

	/**
	 * Actualiza un objetivo de un evaluado (vista del líder). Valida que el
	 * `reviewee_id` pertenezca al evaluador autenticado (403 si no). El body
	 * incluye `reviewee_id` y todos los campos editables del objetivo (no solo
	 * los marcados como requeridos en la documentación). El backend real envuelve
	 * la respuesta en `{ data }`.
	 */
	updateTeamObjective(processId: number | string, objectiveId: number | string, body: any): Observable<any> {
		return this.http.put<any>(
			`${this.base}v1/app/${this.workspaceSlug}/${this.subworkspaceSlug}/team/processes/${processId}/objectives/${objectiveId}/update`,
			body,
		);
	}

	/**
	 * Elimina un objetivo de un evaluado (vista del líder). Valida que el
	 * `reviewee_id` (query, requerido) pertenezca al evaluador autenticado (403 si
	 * no). Devuelve la envoltura `{ message, type, http_code }` de la API real (sin
	 * `data`).
	 */
	deleteTeamObjective(
		processId: number | string,
		objectiveId: number | string,
		revieweeId: number | string,
	): Observable<any> {
		return this.http.delete<any>(
			`${this.base}v1/app/${this.workspaceSlug}/${this.subworkspaceSlug}/team/processes/${processId}/objectives/${objectiveId}/delete?reviewee_id=${revieweeId}`,
		);
	}

	/**
	 * Aprueba (o retira la aprobación de) un objetivo de un evaluado (vista del
	 * líder). El body lleva el `reviewee_id` (requerido, debe pertenecer al
	 * evaluador autenticado) y `approved` (`true` para aprobar, `false` para
	 * retirar la aprobación). Al observar (`approved: false`) con `rejectedData`,
	 * se agrega `rejected_data` al body con el detalle de los campos a cambiar y
	 * el comentario del líder (acción "Observar" del modal de solicitud de
	 * cambios). El backend real envuelve la respuesta en `{ data }` con el nuevo
	 * estado del objetivo (`approved`, `approved_at`, `status`).
	 */
	approveTeamObjective(
		processId: number | string,
		objectiveId: number | string,
		revieweeId: number | string,
		approved: boolean,
		rejectedData?: { comment: string; fields_key: string[] },
	): Observable<any> {
		const body: any = { reviewee_id: revieweeId, approved };
		if (!approved && rejectedData) body.rejected_data = rejectedData;
		return this.http.put<any>(
			`${this.base}v1/app/${this.workspaceSlug}/${this.subworkspaceSlug}/team/processes/${processId}/objectives/${objectiveId}/update-approval`,
			body,
		);
	}

	/**
	 * Registra la nota final de cumplimiento de un objetivo de un evaluado en la
	 * etapa de revisión (vista del líder). El body lleva el `reviewee_id`
	 * (requerido, debe pertenecer al evaluador autenticado), la nota numérica
	 * (`score`) y un comentario opcional. El backend real envuelve la respuesta en
	 * `{ data }` con el objetivo evaluado (`review_score`, `reviewed_at`, `status`).
	 */
	reviewTeamObjective(
		processId: number | string,
		objectiveId: number | string,
		revieweeId: number | string,
		score: number,
		comment?: string,
	): Observable<any> {
		const body: any = { reviewee_id: revieweeId, score };
		if (comment) body.comment = comment;
		return this.http.put<any>(
			`${this.base}v1/app/${this.workspaceSlug}/${this.subworkspaceSlug}/team/processes/${processId}/objectives/${objectiveId}/review`,
			body,
		);
	}



	// ---- Evaluación del equipo (líder → integrante) ------------------------
	// El líder evalúa el cumplimiento de los objetivos y las competencias de cada
	// integrante de su equipo. Endpoints propios de equipo (`team/...`),
	// independientes de la autoevaluación del colaborador (`me/...`).

	/**
	 * Detalle de la evaluación de objetivos que el líder ya realizó sobre un
	 * integrante (lista de objetivos con sus notas). Se filtra por `reviewee_id`.
	 * El backend real envuelve la respuesta en `{ data }`; `data.id` es null cuando
	 * aún no se ha evaluado.
	 */
	getTeamObjectiveReview(processId: number | string, revieweeId: number | string): Observable<any> {
		return this.http.get<any>(
			`${this.base}v1/app/${this.workspaceSlug}/${this.subworkspaceSlug}/team/processes/${processId}/objective-reviews/show?reviewee_id=${revieweeId}`,
		);
	}

	/**
	 * Envía la evaluación de objetivos del líder sobre un integrante: una nota
	 * (`score`), comentario y evidencias (`attachments`) opcionales por cada objetivo
	 * aprobado del proceso. El evaluado va en `reviewee_id` (dentro del payload). Con
	 * evidencias el cuerpo va como `multipart/form-data` (ver {@link buildReviewBody}).
	 * Devuelve la envoltura `{ data, message, type, http_code }` de la API real.
	 */
	submitTeamObjectiveReviews(processId: number | string, payload: TeamObjectiveReviewSubmission): Observable<any> {
		return this.http.post<any>(
			`${this.base}v1/app/${this.workspaceSlug}/${this.subworkspaceSlug}/team/processes/${processId}/objective-reviews/store`,
			this.buildReviewBody(payload),
		);
	}

	/**
	 * Competencias activas del proceso (con su escala y descripciones), como
	 * referencia previa a evaluar a los integrantes del equipo. Son las mismas para
	 * todo el proceso (no hay competencias personalizadas por usuario). Devuelve la
	 * envoltura `{ data }` de la API real.
	 */
	getTeamCompetencies(processId: number | string): Observable<any> {
		return this.http.get<any>(
			`${this.base}v1/app/${this.workspaceSlug}/${this.subworkspaceSlug}/team/processes/${processId}/competencies`,
		);
	}

	/**
	 * Envía la evaluación de competencias del líder sobre un integrante (un valor por
	 * competencia activa del proceso + comentarios y evidencias opcionales). El
	 * evaluado va en `reviewee_id`. Con evidencias el cuerpo va como
	 * `multipart/form-data` (ver {@link buildReviewBody}). Devuelve la envoltura
	 * `{ data, message, type, http_code }`.
	 */
	submitTeamCompetencyReviews(processId: number | string, payload: TeamCompetencyReviewSubmission): Observable<any> {
		return this.http.post<any>(
			`${this.base}v1/app/${this.workspaceSlug}/${this.subworkspaceSlug}/team/processes/${processId}/competency-reviews/store`,
			this.buildReviewBody(payload),
		);
	}

	/**
	 * Detalle de la evaluación de competencias que el líder realizó (o debe realizar)
	 * sobre un integrante: competencias del proceso con el valor asignado, comentario,
	 * escala y estado. `data` es `null` si aún no existe. Envuelto en `{ data, ... }`.
	 */
	getTeamCompetencyReview(processId: number | string, revieweeId: number | string): Observable<any> {
		return this.http.get<any>(
			`${this.base}v1/app/${this.workspaceSlug}/${this.subworkspaceSlug}/team/processes/${processId}/competency-reviews/show?reviewee_id=${revieweeId}`,
		);
	}

	// ---- Equipo (Mi equipo) ------------------------------------------------

	/**
	 * Evaluados a cargo del supervisor en el proceso ("Listar mis evaluados").
	 * El backend responde paginado y envuelto: `{ data: { data: TeamReviewee[], ... } }`.
	 * Soporta filtros opcionales `q` (búsqueda por nombre/email) y `status`
	 * (pending | in_progress | completed). La normalización la hace el servicio.
	 */
	getTeam(
		processId: number | string,
		params?: { q?: string; status?: string; page?: number },
	): Observable<any> {
		// Nota: NO se envía `per_page`; el backend define el tamaño de página.
		const query = new URLSearchParams();
		if (params?.q) query.set("q", params.q);
		if (params?.status) query.set("status", params.status);
		if (params?.page) query.set("page", String(params.page));
		const qs = query.toString() ? `?${query.toString()}` : "";
		return this.http.get<any>(`${this.base}v1/app/${this.workspaceSlug}/${this.subworkspaceSlug}/team/processes/${processId}/reviewees${qs}`);
	}

	/**
	 * Evaluados a cargo del supervisor en la etapa de **revisión/evaluación de
	 * objetivos** ("Listar mis evaluados a calificar"). Endpoint propio de esta
	 * etapa (separado de {@link getTeam}, que alimenta la etapa de creación del
	 * plan), para que ambos flujos puedan divergir sin afectarse. El backend
	 * responde paginado y envuelto igual que `reviewees`: `{ data: { data: [...] } }`.
	 */
	getEvaluationReviewees(
		processId: number | string,
		params?: { q?: string; status?: string; page?: number },
	): Observable<any> {
		const query = new URLSearchParams();
		if (params?.q) query.set("q", params.q);
		if (params?.status) query.set("status", params.status);
		if (params?.page) query.set("page", String(params.page));
		const qs = query.toString() ? `?${query.toString()}` : "";
		return this.http.get<any>(`${this.base}v1/app/${this.workspaceSlug}/${this.subworkspaceSlug}/team/processes/${processId}/reviewees${qs}`);
	}

	/** Aprueba el plan de objetivos de uno o varios miembros. */
	approveTeamGoals(processId: number | string, memberIds: Array<number | string>): Observable<TeamGoalMember[]> {
		return this.http.post<TeamGoalMember[]>(`${this.base}v1/app/${this.workspaceSlug}/${this.subworkspaceSlug}/processes/${processId}/team/approve`, { memberIds });
	}

	// ---- Evaluación de competencias ----------------------------------------

	/** Equipo a evaluar (jefe + pares) en la etapa de evaluación de competencias. */
	getEvaluationTeam(processId: number | string): Observable<CompetencyEvaluationTeam> {
		return this.http.get<CompetencyEvaluationTeam>(`${this.base}v1/app/${this.workspaceSlug}/${this.subworkspaceSlug}/processes/${processId}/evaluation/team`);
	}

	/** Detalle de la evaluación de competencias de una persona. */
	getCompetencyEvaluation(processId: number | string, personId: number | string): Observable<CompetencyEvaluation> {
		return this.http.get<CompetencyEvaluation>(
			`${this.base}v1/app/${this.workspaceSlug}/${this.subworkspaceSlug}/processes/${processId}/evaluation/competencies?personId=${personId}`,
		);
	}

	/**
	 * Competencias a autoevaluar del proceso (con su escala y descripciones).
	 * Devuelve la envoltura `{ data, message, type, http_code }` de la API real.
	 */
	getSelfAssessmentCompetencies(processId: number | string): Observable<any> {
		return this.http.get<any>(
			`${this.base}v1/app/${this.workspaceSlug}/${this.subworkspaceSlug}/me/processes/${processId}/competencies`,
		);
	}

	/**
	 * Autoevaluación de competencias ya realizada del usuario autenticado (si
	 * existe). Devuelve la envoltura `{ data, ... }` de la API real; `data` es null
	 * (o sin competencias) cuando aún no se ha realizado.
	 */
	getSelfAssessmentReview(processId: number | string): Observable<any> {
		return this.http.get<any>(
			`${this.base}v1/app/${this.workspaceSlug}/${this.subworkspaceSlug}/me/processes/${processId}/competency-reviews/show`,
		);
	}

	/**
	 * Envía la autoevaluación de competencias del usuario autenticado (un valor por
	 * competencia activa del proceso + comentarios y evidencias opcionales). Con
	 * evidencias el cuerpo va como `multipart/form-data` (ver {@link buildReviewBody}).
	 * Devuelve la envoltura `{ data, message, type, http_code }` de la API real.
	 */
	submitSelfAssessment(processId: number | string, payload: SelfAssessmentSubmission): Observable<any> {
		return this.http.post<any>(
			`${this.base}v1/app/${this.workspaceSlug}/${this.subworkspaceSlug}/me/processes/${processId}/competency-reviews/store`,
			this.buildReviewBody(payload),
		);
	}

	/**
	 * Actualiza una autoevaluación de competencias ya enviada (solo disponible si el
	 * proceso permite edición posterior a la entrega). Devuelve la envoltura
	 * `{ data, message, type, http_code }` de la API real.
	 *
	 * Si el envío lleva evidencias, va como POST con `_method=PUT`: PHP no parsea el
	 * `multipart/form-data` de un PUT, así que el backend espera el override.
	 */
	updateSelfAssessment(processId: number | string, payload: SelfAssessmentSubmission): Observable<any> {
		const url = `${this.base}v1/app/${this.workspaceSlug}/${this.subworkspaceSlug}/me/processes/${processId}/competency-reviews/update`;
		const body = this.buildReviewBody(payload);

		if (body instanceof FormData) {
			body.append("_method", "PUT");
			return this.http.post<any>(url, body);
		}
		return this.http.put<any>(url, body);
	}

	// ---- PDI (Plan de Desarrollo Individual) -------------------------------
	// Las 12 rutas reales de design.md §4.2, bajo `team/processes/{id}/pdis`
	// (líder) y `me/processes/{id}/pdi` (evaluado, singular: un plan por proceso).

	/** Equipo con el estado de su plan en la etapa PDI (ruta 1). */
	getPdiTeam(processId: number | string): Observable<{ data: PdiTeamResponse }> {
		return this.http.get<{ data: PdiTeamResponse }>(
			`${this.base}v1/app/${this.workspaceSlug}/${this.subworkspaceSlug}/team/processes/${processId}/pdis`,
		);
	}

	/** Detalle agregador de una persona en la etapa PDI (ruta 2). */
	getPdiResult(processId: number | string, revieweeId: number | string): Observable<{ data: PdiResultDetail | null }> {
		return this.http.get<{ data: PdiResultDetail | null }>(
			`${this.base}v1/app/${this.workspaceSlug}/${this.subworkspaceSlug}/team/processes/${processId}/pdis/${revieweeId}/show`,
		);
	}

	/**
	 * Opciones del modal de creación de un objetivo de desarrollo: áreas
	 * (objetivos/competencias del proceso), sugerencias de IsA y catálogo de
	 * cursos enlazables (ruta 3).
	 */
	getPdiGoalFormOptions(
		processId: number | string,
		revieweeId: number | string,
	): Observable<{ data: PdiGoalFormOptions }> {
		return this.http.get<{ data: PdiGoalFormOptions }>(
			`${this.base}v1/app/${this.workspaceSlug}/${this.subworkspaceSlug}/team/processes/${processId}/pdis/goal-options?reviewee_id=${revieweeId}`,
		);
	}

	/** Crea un objetivo de desarrollo en el PDI de una persona (ruta 4). */
	createPdiDevelopmentGoal(
		processId: number | string,
		payload: PdiDevelopmentGoalSubmission,
	): Observable<{ data: PdiDevelopmentGoal }> {
		return this.http.post<{ data: PdiDevelopmentGoal }>(
			`${this.base}v1/app/${this.workspaceSlug}/${this.subworkspaceSlug}/team/processes/${processId}/pdis/development-goals`,
			payload,
		);
	}

	/** Edita un objetivo de desarrollo existente (ruta 5). `reviewee_id` no se acepta al editar. */
	updatePdiDevelopmentGoal(
		processId: number | string,
		goalId: number | string,
		payload: PdiDevelopmentGoalSubmission,
	): Observable<{ data: PdiDevelopmentGoal }> {
		return this.http.put<{ data: PdiDevelopmentGoal }>(
			`${this.base}v1/app/${this.workspaceSlug}/${this.subworkspaceSlug}/team/processes/${processId}/pdis/development-goals/${goalId}/update`,
			payload,
		);
	}

	/** Borra (lógico) un objetivo de desarrollo (ruta 6). */
	deletePdiDevelopmentGoal(processId: number | string, goalId: number | string): Observable<any> {
		return this.http.delete<any>(
			`${this.base}v1/app/${this.workspaceSlug}/${this.subworkspaceSlug}/team/processes/${processId}/pdis/development-goals/${goalId}/delete`,
		);
	}

	/**
	 * Redacción sugerida por IsA para "¿Qué se espera lograr?", a partir de las
	 * áreas que el líder vinculó al objetivo (ruta 7).
	 */
	suggestPdiGoalDescription(
		processId: number | string,
		payload: {
			reviewee_id: number | string;
			areas: Array<{ id: number | string; type: PdiAreaType }>;
			request_id?: string;
			broadcast?: boolean;
		},
	): Observable<{ data: { description: string } }> {
		return this.http.post<{ data: { description: string } }>(
			`${this.base}v1/app/${this.workspaceSlug}/${this.subworkspaceSlug}/team/processes/${processId}/pdis/goal-suggestion`,
			payload,
		);
	}

	/** Con qué se puede agendar la reunión de entrega (ruta 8). */
	getPdiSchedulingOptions(processId: number | string): Observable<{ data: PdiSchedulingOptions }> {
		return this.http.get<{ data: PdiSchedulingOptions }>(
			`${this.base}v1/app/${this.workspaceSlug}/${this.subworkspaceSlug}/team/processes/${processId}/pdis/scheduling-options`,
		);
	}

	/**
	 * Crea/edita la reunión de entrega del plan (ruta 9). `revieweeId` es el id
	 * de usuario del evaluado (el controlador resuelve o crea su plan).
	 */
	savePdiMeeting(
		processId: number | string,
		revieweeId: number | string,
		payload: PdiMeetingSubmission,
	): Observable<{ data: PdiMeeting | null }> {
		return this.http.put<{ data: PdiMeeting | null }>(
			`${this.base}v1/app/${this.workspaceSlug}/${this.subworkspaceSlug}/team/processes/${processId}/pdis/${revieweeId}/meeting`,
			payload,
		);
	}

	/** El líder marca/desmarca un ítem del plan de acción de su equipo (ruta 10). */
	completePdiActionItemTeam(
		processId: number | string,
		goalId: number | string,
		itemId: number | string,
		completed: boolean,
	): Observable<any> {
		return this.http.put<any>(
			`${this.base}v1/app/${this.workspaceSlug}/${this.subworkspaceSlug}/team/processes/${processId}/pdis/development-goals/${goalId}/actions/${itemId}/complete`,
			{ completed },
		);
	}

	/** Mi plan de desarrollo (vista del colaborador en la etapa PDI, ruta 11). */
	getMyPdiPlan(processId: number | string): Observable<{ data: MyPdiPlan }> {
		return this.http.get<{ data: MyPdiPlan }>(
			`${this.base}v1/app/${this.workspaceSlug}/${this.subworkspaceSlug}/me/processes/${processId}/pdi`,
		);
	}

	/**
	 * El evaluado marca/desmarca un paso de su propio plan de acción (ruta 12).
	 * `{ completed: true|false }`, **no** `{ done: … }`: con la llave vieja el
	 * backend (`$request->boolean('completed')`) leería `false` y el tilde no
	 * guardaría nada.
	 */
	updateMyPdiActionStatus(
		processId: number | string,
		goalId: number | string,
		actionId: number | string,
		completed: boolean,
	): Observable<any> {
		return this.http.put<any>(
			`${this.base}v1/app/${this.workspaceSlug}/${this.subworkspaceSlug}/me/processes/${processId}/pdi/goals/${goalId}/actions/${actionId}/complete`,
			{ completed },
		);
	}

	// ---- Feedback (vista del líder) ----------------------------------------
	// Todas las rutas de esta sección son las de la **API v2** y cuelgan de
	// `team/processes/{id}/feedbacks`. Lo que el backend todavía no publica lo
	// responde el mock interceptor con esta misma forma, así que al apagar
	// `MOCK_PERFORMANCE_API` cada endpoint funciona sin tocar código.

	/** Query string `?page=…&per_page=…&filters[x]=…` del listado del equipo. */
	private buildFeedbackTeamQuery(
		page: number,
		perPage: number,
		filters: FeedbackTeamFilters = {},
	): string {
		const params = new URLSearchParams();
		params.set("page", String(page));
		// El backend nombra este parámetro `rowsPerPage`; se envía también como
		// `per_page`, que es como lo llama el resto del módulo.
		// TODO (backend): unificar en `per_page`.
		params.set("per_page", String(perPage));
		params.set("rowsPerPage", String(perPage));
		Object.entries(filters).forEach(([key, value]) => {
			if (value !== undefined && value !== null && value !== "") {
				params.set(`filters[${key}]`, String(value));
			}
		});
		return params.toString();
	}

	/**
	 * Equipo del líder en la etapa de feedback, paginado y filtrado **server-side**.
	 * **API real.** Devuelve las filas tal como las envía el backend: la vista las
	 * lee con esas mismas llaves ({@link FeedbackTeamRow}).
	 *
	 * No es un listado de sesiones: quien todavía no tiene reunión (`feedback` en
	 * `null`) es justamente a quien hay que agendarle.
	 */
	getFeedbackTeam(
		processId: number | string,
		page = 1,
		perPage = 15,
		filters: FeedbackTeamFilters = {},
	): Observable<{ data: FeedbackTeamPage }> {
		const query = this.buildFeedbackTeamQuery(page, perPage, filters);
		return this.http.get<{ data: FeedbackTeamPage }>(
			`${this.base}v1/app/${this.workspaceSlug}/${this.subworkspaceSlug}/team/processes/${processId}/feedbacks?${query}`,
		);
	}

	/**
	 * Con qué se filtra el listado y cuánta gente cae en cada opción: las
	 * pastillas de arriba de la pantalla.
	 *
	 * Los conteos van **siempre sobre el equipo entero**, sin aplicar los filtros
	 * activos, así que se pide una sola vez por proceso.
	 */
	getFeedbackFilters(processId: number | string): Observable<FeedbackFiltersResponse> {
		return this.http
			.get<{ data: FeedbackFiltersResponse }>(
				`${this.base}v1/app/${this.workspaceSlug}/${this.subworkspaceSlug}/team/processes/${processId}/feedbacks/filters`,
			)
			.pipe(map((res) => res?.data));
	}

	/**
	 * Tipos de reunión con el servicio de videollamada disponible para cada uno y
	 * la duración por defecto. Con `can_create_room` en `false` el workspace no
	 * tiene cuentas y la sesión se agenda con el enlace escrito a mano o presencial.
	 */
	getFeedbackSchedulingOptions(
		processId: number | string,
	): Observable<FeedbackSchedulingOptions> {
		return this.http
			.get<{ data: FeedbackSchedulingOptions }>(
				`${this.base}v1/app/${this.workspaceSlug}/${this.subworkspaceSlug}/team/processes/${processId}/feedbacks/scheduling-options`,
			)
			.pipe(map((res) => res?.data));
	}

	/**
	 * La sesión, el resultado del evaluado y la guía de lo que hay que conversar.
	 *
	 * Se identifica con el **id de la sesión** (`feedback.id` de la fila), no con
	 * el del evaluado.
	 *
	 * Lo único que se transforma es la **grabación**: llega con el mismo
	 * envoltorio que `GET …/recording` y se mapea con {@link toFeedbackRecording}
	 * al {@link FeedbackRecording} que consume `app-meeting-recording-panel` —la
	 * excepción a la regla de no mapear que ya documenta el modelo—. Así la vista
	 * sabe si puede grabar sin pedir `…/recording` aparte.
	 */
	getFeedbackDetail(
		processId: number | string,
		feedbackId: number | string,
	): Observable<FeedbackDetailResponse> {
		return this.http
			.get<{ data: Omit<FeedbackDetailResponse, "recording"> & { recording?: FeedbackRecordingResponse | null } }>(
				`${this.base}v1/app/${this.workspaceSlug}/${this.subworkspaceSlug}/team/processes/${processId}/feedbacks/${feedbackId}/show`,
			)
			.pipe(
				map((res) => ({
					...res?.data,
					recording: {
						allowed: !!res?.data?.recording?.allowed,
						blocked_reason: res?.data?.recording?.blocked_reason ?? null,
						recording: this.toFeedbackRecording(res?.data?.recording ?? null),
					},
				})),
			);
	}

	/**
	 * Agenda una sesión con alguien del equipo. El líder autenticado queda como
	 * responsable: `reviewer_id` no se acepta del request.
	 *
	 * El cuerpo va tal como lo espera el backend (`reviewee_id`, `session_type`,
	 * `session_date`, `meeting_link`/`address`, `points`…): las mismas llaves con
	 * las que el listado devuelve después la reunión.
	 */
	saveFeedbackMeeting(
		processId: number | string,
		request: FeedbackMeetingRequest,
	): Observable<unknown> {
		return this.http.post(
			`${this.base}v1/app/${this.workspaceSlug}/${this.subworkspaceSlug}/team/processes/${processId}/feedbacks/store`,
			request,
		);
	}

	/**
	 * La guía que ISA propone a partir de los resultados reales de la persona.
	 *
	 * **Propone y no guarda nada**, tenga o no sesión, así que se puede pedir las
	 * veces que haga falta: los puntos se persisten con el formulario (`points` al
	 * agendar, {@link saveFeedbackPoints} al editar). Se identifica al evaluado con
	 * `reviewee_id`, o con `feedback_id` si la sesión ya existe.
	 */
	previewFeedbackPoints(
		processId: number | string,
		target: { reviewee_id: number | string } | { feedback_id: number | string },
	): Observable<FeedbackPointsPreviewResponse> {
		return this.http
			.post<{ data: FeedbackPointsPreviewResponse }>(
				`${this.base}v1/app/${this.workspaceSlug}/${this.subworkspaceSlug}/team/processes/${processId}/feedbacks/points/preview`,
				target,
			)
			.pipe(map((res) => res?.data));
	}

	/**
	 * Reemplaza la guía de la sesión por la que se envía. Los puntos que llegan
	 * con `id` conservan su marcado; los que no vienen en la lista **se eliminan**.
	 * El orden del arreglo es el orden en que se van a tratar.
	 *
	 * Las notas de la reunión van por bloque (`block_notes`), no por punto.
	 */
	saveFeedbackPoints(
		processId: number | string,
		feedbackId: number | string,
		request: FeedbackPointsRequest,
	): Observable<FeedbackPointsResponse> {
		return this.http
			.put<{ data: FeedbackPointsResponse }>(
				`${this.base}v1/app/${this.workspaceSlug}/${this.subworkspaceSlug}/team/processes/${processId}/feedbacks/${feedbackId}/points`,
				request,
			)
			.pipe(map((res) => res?.data));
	}

	/**
	 * Marca o desmarca un punto de la guía mientras se conduce la reunión. Es lo
	 * que después permite saber qué quedó sin conversar; desmarcar borra también
	 * quién lo había marcado.
	 */
	setFeedbackPointChecked(
		processId: number | string,
		feedbackId: number | string,
		pointId: number | string,
		checked: boolean,
	): Observable<FeedbackPointsProgress> {
		return this.http
			.put<{ data: FeedbackPointsProgress }>(
				`${this.base}v1/app/${this.workspaceSlug}/${this.subworkspaceSlug}/team/processes/${processId}/feedbacks/${feedbackId}/points/${pointId}/check`,
				{ checked },
			)
			.pipe(map((res) => res?.data));
	}

	/**
	 * Marca el inicio de la reunión: pasa la sesión a `started` y sella
	 * `started_at`. Es también lo que **reabre** una sesión `overdue` (vencida =
	 * nunca se juntaron), así que se puede llamar más de una vez sin que sea un
	 * error.
	 *
	 * Devuelve la sesión completa, con el `status` ya movido.
	 */
	startFeedbackSession(
		processId: number | string,
		feedbackId: number | string,
	): Observable<FeedbackDetailSession> {
		return this.http
			.put<{ data: FeedbackDetailSession }>(
				`${this.base}v1/app/${this.workspaceSlug}/${this.subworkspaceSlug}/team/processes/${processId}/feedbacks/${feedbackId}/start`,
				{},
			)
			.pipe(map((res) => res?.data));
	}

	/**
	 * Cierra la reunión a mano: pasa la sesión a `completed` con `finished_at`,
	 * `finished_by` y `finished_source: "manual"`.
	 *
	 * **Idempotente a propósito**: reintentar el cierre —o el gestor y la app
	 * cerrando la misma sesión casi al mismo tiempo— no reescribe nada ni dispara
	 * un segundo aviso. Responde **422** con el detalle de `finish/preflight` si
	 * todavía hay un bloqueo; el mensaje del backend es lo que se le muestra al
	 * líder.
	 */
	finishFeedbackSession(
		processId: number | string,
		feedbackId: number | string,
	): Observable<FeedbackDetailSession> {
		return this.http
			.put<{ data: FeedbackDetailSession }>(
				`${this.base}v1/app/${this.workspaceSlug}/${this.subworkspaceSlug}/team/processes/${processId}/feedbacks/${feedbackId}/finish`,
				{},
			)
			.pipe(map((res) => res?.data));
	}

	/**
	 * Arma el {@link FeedbackRecording} que consume `app-meeting-recording-panel`
	 * a partir de lo que devuelven los dos endpoints de grabación.
	 *
	 * La transcripción y el resumen son **dos llaves distintas** (`transcript` y
	 * `summary`) y cada una pasa a la suya: la vista muestra el resumen y ofrece la
	 * transcripción aparte, en un desplegable.
	 *
	 * `summaryStatus` **se deriva** en vez de leerse, así da igual qué valores
	 * tomen `status` y `summary_status`, que no tienen lista cerrada:
	 *
	 * - `transcription_error` ⇒ `failed` (ni transcripción ni resumen).
	 * - `summary` ⇒ `ready`.
	 * - `summary_pending` ⇒ `processing` (IsA todavía redacta el resumen).
	 * - sin `transcript` ⇒ `processing` (todavía transcribe).
	 * - el resto ⇒ `none`: transcripción lista y el backend no generó resumen.
	 */
	private toFeedbackRecording(res: FeedbackRecordingResponse | null): FeedbackRecording | null {
		const recording = res?.recording;
		if (!recording) return null;

		const summaryStatus: FeedbackRecording["summaryStatus"] = recording.transcription_error
			? "failed"
			: recording.summary
				? "ready"
				: recording.summary_pending || !recording.transcript
					? "processing"
					: "none";

		return {
			id: recording.id,
			audioUrl: recording.url ?? "",
			// El backend no devuelve el MIME; nadie lo consume (el reproductor solo
			// necesita la URL), así que se deja vacío.
			mimeType: "",
			durationSeconds: recording.duration_seconds ?? 0,
			recordedAt: recording.recorded_at ?? "",
			summary: recording.summary ?? null,
			transcript: recording.transcript ?? null,
			summaryStatus,
		};
	}

	/**
	 * Sube el audio de la reunión de feedback grabado por el líder.
	 *
	 * Va como `multipart/form-data` (no base64): una reunión de 40 minutos pesa
	 * decenas de MB y serializarla inflaría un tercio más el payload y la memoria.
	 * Por eso **no se fija `Content-Type`**: el navegador lo genera con su
	 * `boundary` (los interceptores globales lo respetan al detectar un `FormData`).
	 *
	 * Responde **422** si la sesión no permite grabación. El backend guarda el
	 * audio y **encola** la transcripción, así que vuelve con `transcript: null`:
	 * el resumen se consulta después con {@link getFeedbackRecording}.
	 */
	uploadFeedbackRecording(
		processId: number | string,
		feedbackId: number | string,
		recording: { blob: Blob; fileName: string; mimeType: string; durationSeconds: number },
	): Observable<FeedbackRecording | null> {
		const form = new FormData();
		form.append("duration_seconds", String(recording.durationSeconds));
		form.append("recording", recording.blob, recording.fileName);

		return this.http
			.post<{ data: FeedbackRecordingResponse }>(
				`${this.base}v1/app/${this.workspaceSlug}/${this.subworkspaceSlug}/team/processes/${processId}/feedbacks/${feedbackId}/recording`,
				form,
			)
			.pipe(map((res) => this.toFeedbackRecording(res?.data)));
	}

	/**
	 * Grabación de la sesión y el estado de su transcripción. `null` si aún no se
	 * grabó. Se consulta en bucle mientras `summaryStatus` siga en `processing`.
	 *
	 * ⚠️ La `url` del audio es **firmada y expira a los 15 minutos**.
	 */
	getFeedbackRecording(
		processId: number | string,
		feedbackId: number | string,
	): Observable<FeedbackRecording | null> {
		return this.http
			.get<{ data: FeedbackRecordingResponse }>(
				`${this.base}v1/app/${this.workspaceSlug}/${this.subworkspaceSlug}/team/processes/${processId}/feedbacks/${feedbackId}/recording`,
			)
			.pipe(map((res) => this.toFeedbackRecording(res?.data)));
	}

	/**
	 * Si la sesión admite grabación y cuál es la que ya tiene, en **una sola**
	 * lectura: `allowed` y la grabación salen del mismo GET, así que la vista lo
	 * pide una vez al cargar en vez de preguntar dos veces lo mismo.
	 */
	getFeedbackRecordingState(
		processId: number | string,
		feedbackId: number | string,
	): Observable<FeedbackRecordingState> {
		return this.http
			.get<{ data: FeedbackRecordingResponse }>(
				`${this.base}v1/app/${this.workspaceSlug}/${this.subworkspaceSlug}/team/processes/${processId}/feedbacks/${feedbackId}/recording`,
			)
			.pipe(
				map((res) => ({
					allowed: !!res?.data?.allowed,
					blocked_reason: res?.data?.blocked_reason ?? null,
					recording: this.toFeedbackRecording(res?.data),
				})),
			);
	}

	// ---- Feedback (vista del colaborador) ----------------------------------

	/**
	 * Sesión de feedback del evaluado ("Mi feedback"): cuándo, con quién, dónde o
	 * por qué enlace, la grabación y qué conformidad se le pide.
	 *
	 * El evaluado tiene **una sola** sesión por proceso, así que no hay listado ni
	 * `feedback_id` en la ruta: la resuelve el usuario autenticado. Si todavía no le
	 * agendaron ninguna, responde `data.feedback: null` — **no es un error**.
	 *
	 * No trae la guía de puntos (es la conducción del líder). Se devuelve tal cual
	 * llega: la vista lee las llaves del backend ({@link MyFeedbackResponse}).
	 */
	getMyFeedback(processId: number | string): Observable<{ data: MyFeedbackResponse }> {
		return this.http.get<{ data: MyFeedbackResponse }>(
			`${this.base}v1/app/${this.workspaceSlug}/${this.subworkspaceSlug}/me/processes/${processId}/feedback`,
		);
	}

	/**
	 * Registra la conformidad del evaluado sobre una sesión de feedback ya
	 * realizada. Sin `feedback_id` en la ruta, igual que {@link getMyFeedback}: la
	 * sesión la resuelve el usuario autenticado. Lo registrado se refleja después en
	 * el GET (`accepted`, `accepted_at`, `signature`).
	 *
	 * El backend responde 422 si el líder aún no cerró la sesión o si esta no pide
	 * conformidad.
	 *
	 * `comment` viaja vacío a propósito (la UI no lo pide) y `signature` es una
	 * llave **extra**: hoy el backend la descarta, pero está acordado que la reciba
	 * más adelante para guardar el trazo. No quitarla.
	 */
	acceptMyFeedback(
		processId: number | string,
		request: MyFeedbackAcceptanceRequest,
	): Observable<any> {
		return this.http.post<any>(
			`${this.base}v1/app/${this.workspaceSlug}/${this.subworkspaceSlug}/me/processes/${processId}/feedback/accept`,
			request,
		);
	}

	// ---- Calibración (etapa "calibration", rol líder) -----------------------
	// TODO (backend): la etapa completa está pendiente de publicar. Las URLs de
	// abajo son el contrato propuesto (mismo estilo que el resto de endpoints de
	// equipo); hoy las responde el mock interceptor.

	/** Evaluados que el líder debe calibrar en el proceso. */
	getCalibrationTeam(processId: number | string): Observable<{ data: CalibrationTeamMember[] }> {
		return this.http.get<{ data: CalibrationTeamMember[] }>(
			`${this.base}v1/app/${this.workspaceSlug}/${this.subworkspaceSlug}/team/processes/${processId}/calibrations`,
		);
	}

	/**
	 * Catálogo de las nueve cajas de la matriz 9-box del proceso.
	 *
	 * Va en su propio endpoint —y no dentro del listado— porque las cajas existen
	 * aunque nadie esté ubicado en ellas: es lo que permite pintar la matriz
	 * completa, con sus casilleros vacíos.
	 */
	getCalibrationNineBoxes(processId: number | string): Observable<{ data: CalibrationNineBox[] }> {
		return this.http.get<{ data: CalibrationNineBox[] }>(
			`${this.base}v1/app/${this.workspaceSlug}/${this.subworkspaceSlug}/team/processes/${processId}/calibrations/boxes`,
		);
	}

	/**
	 * Detalle de calibración de un evaluado: cabecera, puntajes, objetivos y
	 * competencias con su escala y lo reportado, reunión y grabación.
	 */
	getCalibrationDetail(
		processId: number | string,
		revieweeId: number | string,
	): Observable<{ data: CalibrationDetail }> {
		return this.http.get<{ data: CalibrationDetail }>(
			`${this.base}v1/app/${this.workspaceSlug}/${this.subworkspaceSlug}/team/processes/${processId}/calibrations/show?reviewee_id=${revieweeId}`,
		);
	}

	/**
	 * Guarda la calibración de un objetivo o de una competencia.
	 *
	 * Pasa por {@link buildReviewBody}: si el líder adjuntó evidencias, el envío se
	 * convierte a `multipart/form-data` (`attachments[]`) igual que las cuatro APIs
	 * de evaluación; sin archivos sigue siendo JSON.
	 *
	 * Responde con el **detalle completo ya recalculado**, porque cambiar la nota de
	 * un ítem mueve los puntajes de objetivos/competencias y el global: que los
	 * recalcule el backend evita que el front replique su fórmula de ponderación.
	 */
	saveCalibrationItem(
		processId: number | string,
		payload: CalibrationItemSubmission,
	): Observable<{ data: CalibrationDetail }> {
		return this.http.put<{ data: CalibrationDetail }>(
			`${this.base}v1/app/${this.workspaceSlug}/${this.subworkspaceSlug}/team/processes/${processId}/calibrations/items`,
			this.buildReviewBody(payload),
		);
	}

	/**
	 * Sube el audio de la reunión de calibración. Mismas consideraciones que
	 * {@link uploadFeedbackRecording}: multipart, sin fijar `Content-Type`, el
	 * backend responde con la grabación en estado `processing` y la respuesta
	 * llega con el envoltorio `{ data: { allowed, blocked_reason, recording } }`,
	 * que se mapea con {@link toFeedbackRecording} igual que en feedback.
	 *
	 * ⚠️ La llave del archivo es **`audio`** y además viaja `mime_type`; en
	 * feedback son `recording` y sin `mime_type`. La asimetría es del contrato
	 * propuesto, no de este servicio: confirmarla al publicar el endpoint.
	 */
	uploadCalibrationRecording(
		processId: number | string,
		revieweeId: number | string,
		recording: { blob: Blob; fileName: string; mimeType: string; durationSeconds: number },
	): Observable<FeedbackRecording | null> {
		const form = new FormData();
		form.append("reviewee_id", String(revieweeId));
		form.append("duration_seconds", String(recording.durationSeconds));
		form.append("mime_type", recording.mimeType);
		form.append("audio", recording.blob, recording.fileName);

		return this.http
			.post<{ data: FeedbackRecordingResponse }>(
				`${this.base}v1/app/${this.workspaceSlug}/${this.subworkspaceSlug}/team/processes/${processId}/calibrations/recording`,
				form,
			)
			.pipe(map((res) => this.toFeedbackRecording(res?.data)));
	}

	/**
	 * Grabación de la reunión de calibración de un evaluado y el estado del resumen
	 * de IsA. `null` si aún no se grabó; se consulta en bucle mientras
	 * `summaryStatus` siga en `processing`.
	 */
	getCalibrationRecording(
		processId: number | string,
		revieweeId: number | string,
	): Observable<FeedbackRecording | null> {
		return this.http
			.get<{ data: FeedbackRecordingResponse }>(
				`${this.base}v1/app/${this.workspaceSlug}/${this.subworkspaceSlug}/team/processes/${processId}/calibrations/recording?reviewee_id=${revieweeId}`,
			)
			.pipe(map((res) => this.toFeedbackRecording(res?.data)));
	}

	// ---- Encuesta del proceso ----------------------------------------------
	// TODO (backend): endpoints pendientes de publicar. Las URLs ya reflejan el
	// contrato esperado; hoy las responde el mock interceptor.

	/**
	 * Encuesta de cierre del proceso que le corresponde responder al usuario
	 * autenticado (etapa `survey`), con sus preguntas y el estado (`pending` /
	 * `completed`). El backend real envuelve la respuesta en `{ data }`.
	 */
	getProcessSurvey(processId: number | string): Observable<{ data: PerformanceSurvey }> {
		return this.http.get<{ data: PerformanceSurvey }>(
			`${this.base}v1/app/${this.workspaceSlug}/${this.subworkspaceSlug}/me/processes/${processId}/survey`,
		);
	}

	/**
	 * Envía las respuestas de la encuesta del proceso. Devuelve la encuesta ya en
	 * estado `completed` (envuelta en `{ data }`), para que la vista pase sola al
	 * estado de agradecimiento sin volver a pedirla.
	 */
	submitProcessSurvey(
		processId: number | string,
		payload: SurveySubmission,
	): Observable<{ data: PerformanceSurvey }> {
		return this.http.post<{ data: PerformanceSurvey }>(
			`${this.base}v1/app/${this.workspaceSlug}/${this.subworkspaceSlug}/me/processes/${processId}/survey/store`,
			payload,
		);
	}
}
